import { useEffect, useRef, useState, useCallback } from "react";
import { UploadCloud, Trash2, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];
const MAX_IMG_MB = 4;
const MAX_VID_MB = 50;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function fullUrl(u) { return u.startsWith("http") ? u : `${BACKEND}${u}`; }

export function ProfileMediaGallery() {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/me/media")
      .then(({ data }) => setItems(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFiles = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    for (const file of list) {
      const isVideo = VIDEO_TYPES.includes(file.type);
      const isImage = IMAGE_TYPES.includes(file.type);
      if (!isImage && !isVideo) { toast.error(`Skipped ${file.name}: unsupported type`); continue; }
      const limit = (isVideo ? MAX_VID_MB : MAX_IMG_MB) * 1024 * 1024;
      if (file.size > limit) { toast.error(`${file.name} exceeds ${isVideo ? MAX_VID_MB : MAX_IMG_MB}MB`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setItems((prev) => [data, ...prev]);
      } catch (err) { toast.error(apiError(err)); }
    }
    setUploading(false);
    toast.success("Upload complete");
  }, []);

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer?.files); };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.original_filename || "this item"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/uploads/media/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success("Removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="profile-media-gallery">
      <div className="overline mb-2">Photo &amp; video gallery</div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className="relative rounded-xl border-2 border-dashed cursor-pointer p-6 text-center"
        style={{
          borderColor: dragOver ? "#E040FB" : "var(--border-soft)",
          background: dragOver ? "rgba(224,64,251,0.06)" : "#0b0b0f",
          transition: "border-color 150ms ease, background 150ms ease",
        }}
        data-testid="media-dropzone"
      >
        <input
          type="file"
          accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(",")}
          multiple
          ref={inputRef}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="media-dropzone-input"
        />
        {uploading ? (
          <div className="flex flex-col items-center" style={{ color: "var(--ink-soft)" }}>
            <Loader2 size={22} className="animate-spin mb-2 rainbow-text" />
            Uploading…
          </div>
        ) : (
          <div style={{ color: "var(--ink-soft)" }}>
            <UploadCloud size={26} className="mx-auto mb-2 rainbow-text" />
            <div className="text-sm"><span className="rainbow-text font-bold">Drop</span> photos or videos here or <span className="rainbow-text font-bold">click</span> to browse</div>
            <div className="text-xs mt-1" style={{ color: "var(--ink-muted)" }}>
              Images up to {MAX_IMG_MB}MB · Videos up to {MAX_VID_MB}MB · you can pick many at once
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-4 text-sm" style={{ color: "var(--ink-muted)" }}>Loading gallery…</div>
      ) : items.length === 0 ? (
        <div className="mt-4 text-sm" style={{ color: "var(--ink-muted)" }}>Nothing here yet — drop something in to get started.</div>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="relative rounded-xl overflow-hidden group" style={{ border: "1px solid var(--border-soft)", background: "#0b0b0f" }} data-testid={`media-item-${m.id}`}>
              {m.kind === "video" ? (
                <div className="relative">
                  <video src={fullUrl(m.url)} className="w-full aspect-square object-cover" muted playsInline preload="metadata" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="rounded-full p-2 bg-black/60"><Play size={18} className="rainbow-text" /></div>
                  </div>
                </div>
              ) : (
                <img src={fullUrl(m.url)} alt="" className="w-full aspect-square object-cover" />
              )}
              <button
                type="button"
                onClick={() => remove(m)}
                className="absolute top-2 right-2 rounded-full p-1.5 btn-press opacity-0 group-hover:opacity-100"
                style={{ background: "rgba(0,0,0,0.75)", color: "#FF3B3B", transition: "opacity 150ms ease" }}
                data-testid={`media-delete-${m.id}`}
                title="Delete"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PublicMediaGallery({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/users/${userId}/media`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="mt-10" data-testid="public-media-gallery">
      <div className="overline mb-2">Gallery</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {items.map((m) => (
          <div key={m.id} className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-soft)", background: "#0b0b0f" }} data-testid={`public-media-item-${m.id}`}>
            {m.kind === "video" ? (
              <video src={fullUrl(m.url)} className="w-full aspect-square object-cover" controls playsInline preload="metadata" />
            ) : (
              <img src={fullUrl(m.url)} alt="" className="w-full aspect-square object-cover" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
